import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { Session } from '@supabase/supabase-js'
import type { ConnectedRepo, FileNode, AppView } from '@/types'

interface AppState {
  // Auth
  session:       Session | null
  isAuthLoading: boolean

  // Navigation
  view:             AppView
  selectedRepo:     ConnectedRepo | null
  selectedFilePath: string | null
  selectedFileSha:  string | null

  // Data
  repos:         ConnectedRepo[]
  fileTree:      FileNode[]
  markdownContent: string | null

  // Loading
  isLoadingTree: boolean
  isLoadingFile: boolean

  // Error
  error: string | null

  // ── Actions ──────────────────────────────────────────────────────────────
  setSession:       (session: Session | null) => void
  setAuthLoading:   (loading: boolean) => void
  setRepos:         (repos: ConnectedRepo[]) => void
  selectRepo:       (repo: ConnectedRepo) => void
  setFileTree:      (tree: FileNode[]) => void
  selectFile:       (path: string, sha: string) => void
  setMarkdownContent: (content: string | null) => void
  setLoadingTree:   (loading: boolean) => void
  setLoadingFile:   (loading: boolean) => void
  setError:         (error: string | null) => void
  backToVault:      () => void
  backToWorkspace:  () => void
  refreshTree:      () => void

  // ── ExPedition ───────────────────────────────────────────────────────────
  expedition:           FileNode[]
  expeditionName:       string
  addToExpedition:      (file: FileNode) => void
  removeFromExpedition: (path: string)   => void
  reorderExpedition:    (fromIdx: number, toIdx: number) => void
  clearExpedition:      () => void
  setExpeditionName:    (name: string)   => void
}

export const useAppStore = create<AppState>()(
  devtools(
    (set, get) => ({
      session:          null,
      isAuthLoading:    true,
      view:             'vault',
      selectedRepo:     null,
      selectedFilePath: null,
      selectedFileSha:  null,
      repos:            [],
      fileTree:        [],
      markdownContent: null,
      isLoadingTree:   false,
      isLoadingFile:   false,
      error:           null,

      setSession:       (session)     => set({ session }),
      setAuthLoading:   (isAuthLoading) => set({ isAuthLoading }),
      setRepos:         (repos)       => set({ repos }),

      selectRepo: (repo) => set({
        selectedRepo:     repo,
        view:             'workspace',
        selectedFilePath: null,
        selectedFileSha:  null,
        markdownContent:  null,
        fileTree:         [],
        isLoadingTree:    true,
        error:            null,
      }),

      setFileTree: (fileTree) => set({ fileTree, isLoadingTree: false }),

      selectFile: (path, sha) => {
        // Persist last-opened file per repo to localStorage
        const repo = get().selectedRepo
        if (repo?.id) {
          try {
            const map = JSON.parse(
              localStorage.getItem('exmarked:lastOpened') ?? '{}'
            ) as Record<string, { path: string }>
            map[repo.id] = { path }
            localStorage.setItem('exmarked:lastOpened', JSON.stringify(map))
          } catch { /* ignore */ }
        }
        set({
          selectedFilePath: path,
          selectedFileSha:  sha,
          view:             'viewer',
          markdownContent:  null,
          isLoadingFile:    true,
          error:            null,
        })
      },

      setMarkdownContent: (markdownContent) => set({
        markdownContent,
        isLoadingFile: false,
      }),

      setLoadingTree: (isLoadingTree) => set({ isLoadingTree }),
      setLoadingFile: (isLoadingFile) => set({ isLoadingFile }),
      setError:       (error)         => set({ error }),

      backToVault: () => set({
        view:             'vault',
        selectedRepo:     null,
        selectedFilePath: null,
        selectedFileSha:  null,
        markdownContent:  null,
        fileTree:         [],
        isLoadingTree:    false,
        isLoadingFile:    false,
        error:            null,
      }),

      backToWorkspace: () => set({
        view:             'workspace',
        selectedFilePath: null,
        selectedFileSha:  null,
        markdownContent:  null,
        isLoadingFile:    false,
        error:            null,
      }),

      // Re-fetches the tree without clearing the current file selection.
      // The Sidebar effect detects SHA changes and re-fetches content if needed.
      refreshTree: () => set({ isLoadingTree: true }),

      // ── ExPedition ────────────────────────────────────────────────────────
      expedition:     [],
      expeditionName: '',

      addToExpedition: (file) => set((s) => ({
        expedition: s.expedition.some((f) => f.path === file.path)
          ? s.expedition
          : [...s.expedition, file],
      })),

      removeFromExpedition: (path) => set((s) => ({
        expedition: s.expedition.filter((f) => f.path !== path),
      })),

      reorderExpedition: (fromIdx, toIdx) => set((s) => {
        const items = [...s.expedition]
        const [moved] = items.splice(fromIdx, 1)
        items.splice(toIdx, 0, moved)
        return { expedition: items }
      }),

      clearExpedition:   () => set({ expedition: [], expeditionName: '' }),
      setExpeditionName: (expeditionName) => set({ expeditionName }),
    }),
    { name: 'ExMarked' }
  )
)
